import React from "react";
import {
  FaCalendarAlt,
  FaEdit,
  FaExternalLinkAlt,
  FaFileImage,
  FaMedal,
  FaTrash,
} from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { GiPodium } from "react-icons/gi";

import {
  formatPalmaresShortDate,
  getPalmaresKey,
  getRankBadgeClass,
  getRankLabel,
  getResultTokenClass,
  getResultTokenLabel,
  parseResultTokens,
} from "../utils/palmaresUtils";

function ResultTokens({ resultats }) {
  const tokens = parseResultTokens(resultats);

  if (tokens.length === 0) {
    return (
      <span className="text-xs font-semibold text-slate-400">
        Non renseigné
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {tokens.map((token, index) => (
        <span
          key={`${token}-${index}`}
          title={getResultTokenLabel(token)}
          className={`inline-flex h-8 min-w-8 items-center justify-center rounded-xl px-2 text-xs font-bold ${getResultTokenClass(
            token,
          )}`}
        >
          {token}
        </span>
      ))}
    </div>
  );
}

function PalmaresTable({
  palmares = [],
  deletingIds = [],
  onEdit,
  onDelete,
  onImageClick,
  onOpenLink,
}) {
  return (
    <div className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
      <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600 dark:text-amber-200">
            Tableau du palmarès
          </p>

          <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
            Résultats enregistrés
          </h2>
        </div>

        <p className="text-sm font-bold text-slate-500 dark:text-white/50">
          {palmares.length} ligne{palmares.length > 1 ? "s" : ""}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1050px] w-full border-collapse">
          <thead className="bg-white dark:bg-slate-900">
            <tr className="border-b border-slate-200 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-white/10">
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4">Compétition</th>
              <th className="px-5 py-4">Classement</th>
              <th className="px-5 py-4">Combats</th>
              <th className="px-5 py-4">Observations</th>
              <th className="px-5 py-4">Documents</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {palmares.map((item, index) => {
              const itemKey = getPalmaresKey(item, index);
              const isDeleting = deletingIds.includes(itemKey);
              const podiumUrl = item?.podium_palmares;
              const tableauUrl = item?.tableau_palmares;

              return (
                <tr
                  key={itemKey}
                  className="border-b border-slate-100 transition hover:bg-amber-50/50 dark:border-white/5 dark:hover:bg-white/[0.03]"
                >
                  <td className="px-5 py-4 align-top">
                    <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/70">
                      <FaCalendarAlt className="text-amber-600 dark:text-amber-200" />
                      {formatPalmaresShortDate(item?.date_palmares)}
                    </div>
                  </td>

                  <td className="px-5 py-4 align-top">
                    <p className="max-w-[260px] font-titre font-semibold leading-6 text-slate-950 dark:text-white">
                      {item?.titre_palmares || "Résultat sans titre"}
                    </p>
                  </td>

                  <td className="px-5 py-4 align-top">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${getRankBadgeClass(
                        item?.classement_palmares,
                      )}`}
                    >
                      <FaMedal />
                      {getRankLabel(item?.classement_palmares)}
                    </span>
                  </td>

                  <td className="px-5 py-4 align-top">
                    <ResultTokens resultats={item?.resultats_palmares} />
                  </td>

                  <td className="px-5 py-4 align-top">
                    <p className="max-w-[280px] line-clamp-3 text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                      {item?.observations_palmares ||
                        "Aucune observation renseignée."}
                    </p>
                  </td>

                  <td className="px-5 py-4 align-top">
                    <div className="flex flex-wrap gap-2">
                      {podiumUrl && (
                        <button
                          type="button"
                          onClick={() => onImageClick(podiumUrl)}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-amber-100 px-3 py-2 text-xs font-bold text-amber-700 transition hover:-translate-y-0.5 dark:bg-amber-500/10 dark:text-amber-200"
                        >
                          <GiPodium />
                          Podium
                        </button>
                      )}

                      {tableauUrl && (
                        <button
                          type="button"
                          onClick={() => onOpenLink(tableauUrl)}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-sky-100 px-3 py-2 text-xs font-bold text-sky-700 transition hover:-translate-y-0.5 dark:bg-sky-500/10 dark:text-sky-200"
                        >
                          <FaTableList />
                          Tableau
                          <FaExternalLinkAlt className="text-[10px]" />
                        </button>
                      )}

                      {!podiumUrl && !tableauUrl && (
                        <span className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-400 dark:bg-white/10 dark:text-white/40">
                          <FaFileImage />
                          Aucun
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-5 py-4 align-top">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(item)}
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-amber-500 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-amber-600"
                      >
                        <FaEdit />
                        Modifier
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        disabled={isDeleting}
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
                      >
                        <FaTrash />
                        {isDeleting ? "Suppression..." : "Supprimer"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PalmaresTable;